import { ApiTypes, MediaQuery, MediaView, ResGetMedia } from 'common';
import { Call } from '@src/services/api';
import {ResRightRequest, SimilarRequestType} from '@src/types/api';

export const getMedia = async (mediaId: string, transcriptionLanguage?: string): Promise<MediaView> => {
  const query: MediaQuery = {
    filter: {
      ids: [mediaId],
    },
    withTranscription: true,
    transcriptionLanguage,
  };
  const res = await searchMedias(query);
  const [media] = res.media;

  return media;
};

export interface SubtitleTrack {
  language: string;
  type?: string;
  url: string;
}

export interface TranslationLanguage {
  iso6393: string;
  providerCode: string;
  displayName: string;
}

export type SubtitleTranslationStatus =
  | 'queued'
  | 'processing'
  | 'uploaded'
  | 'indexing'
  | 'done'
  | 'error';

export interface SubtitleTranslationJob {
  jobId: string;
  targetLanguage: string;
  sourceLanguage?: string;
  status: SubtitleTranslationStatus;
  errorCode?: string;
  updatedAt?: string;
}

function requireString(value: unknown, field: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`Invalid subtitle response: missing ${field}`);
  }
  return value;
}

function parseTrack(value: unknown): SubtitleTrack {
  const track = value as Partial<SubtitleTrack> | null;
  return {
    language: requireString(track?.language, 'language'),
    type: track?.type === undefined ? undefined : requireString(track.type, 'type'),
    url: requireString(track?.url, 'url'),
  };
}

function parseLanguage(value: unknown): TranslationLanguage {
  const language = value as Partial<TranslationLanguage> | null;
  return {
    iso6393: requireString(language?.iso6393, 'iso6393'),
    providerCode: requireString(language?.providerCode, 'providerCode'),
    displayName: requireString(language?.displayName, 'displayName'),
  };
}

function parseJob(value: unknown): SubtitleTranslationJob {
  const job = value as Partial<SubtitleTranslationJob> | null;
  const status = job?.status;
  if (!['queued', 'processing', 'uploaded', 'indexing', 'done', 'error'].includes(status as string)) {
    throw new Error('Invalid subtitle response: missing status');
  }
  return {
    jobId: requireString(job?.jobId, 'jobId'),
    targetLanguage: requireString(job?.targetLanguage, 'targetLanguage'),
    sourceLanguage: job?.sourceLanguage === undefined ? undefined : requireString(job.sourceLanguage, 'sourceLanguage'),
    status: status as SubtitleTranslationStatus,
    errorCode: job?.errorCode === undefined ? undefined : requireString(job.errorCode, 'errorCode'),
    updatedAt: job?.updatedAt === undefined ? undefined : requireString(job.updatedAt, 'updatedAt'),
  };
}

export const getSubtitleTracks = async (mediaId: string): Promise<SubtitleTrack[]> => {
  const response = await Call.get<{ tracks?: unknown[]; subtitles?: unknown[] }>(`/api/media/${mediaId}/subtitles`);
  const tracks = response?.tracks ?? response?.subtitles;
  if (!Array.isArray(tracks)) {
    throw new Error('Invalid subtitle response: missing subtitles');
  }
  return tracks.map(parseTrack);
};

export const getTranslationLanguages = async (): Promise<TranslationLanguage[]> => {
  const response = await Call.get<{ languages: unknown[] }>('/api/media/subtitles/translation-languages');
  if (!response || !Array.isArray(response.languages)) {
    throw new Error('Invalid subtitle response: missing languages');
  }
  return response.languages.map(parseLanguage);
};

export const getSubtitleTranslationJobs = async (mediaId: string): Promise<SubtitleTranslationJob[]> => {
  const response = await Call.get<{ jobs: unknown[] }>(`/api/media/${mediaId}/subtitles/translations`);
  if (!response || !Array.isArray(response.jobs)) {
    throw new Error('Invalid subtitle response: missing jobs');
  }
  return response.jobs.map(parseJob);
};

export const getSubtitleTranslationJob = async (mediaId: string, jobId: string): Promise<SubtitleTranslationJob> =>
  parseJob(await Call.get<unknown>(`/api/media/${mediaId}/subtitles/translations/${jobId}`));

export const createSubtitleTranslation = (
  mediaId: string,
  targetLanguage: string,
): Promise<SubtitleTranslationJob> =>
  Call.post<unknown, { targetLanguage: string }>(
    `/api/media/${mediaId}/subtitles/translations`,
    { targetLanguage },
  ).then(parseJob);

export const searchMedias = async (query: MediaQuery): Promise<ResGetMedia> =>
  Call.post<ResGetMedia, MediaQuery>('/api/media/search', query);

export const updateProductMediaCount = async (query: MediaQuery): Promise<void> =>
  Call.post<void, MediaQuery>('/api/media/update-linked-products-exact', query);

export const getMediaCarousel = (mediaId: string): Promise<MediaView[]> =>
  Call.get<MediaView[], { target: string }>('/api/media/carousel', {
    target: mediaId,
  });

export const findMediaSimilar = (
  mediaId: string,
  similiType: SimilarRequestType[],
  stringifiedQuery?: string
): Promise<MediaView[]> =>
  Call.get<
    MediaView[],
    { target: string; similiType: typeof similiType; stringifiedQuery?: string }
  >('/api/media/similar', {
    target: mediaId,
    similiType,
    stringifiedQuery,
  });

export const updateObjectLabels = (params: ApiTypes.ParamsPutObjectLabels): Promise<void> =>
  Call.put<ApiTypes.ParamsPutObjectLabels>('/api/media/object_labels', params);

export const countRightsRequest = (): Promise<number> => Call.get<number>(`/api/media/count-rights-request`);

export const getRightsRequest = (): Promise<ResRightRequest> => Call.get<ResRightRequest>(`/api/media/rights-request`);

export const createRightsRequest = (
  mediaId: string,
  params: ApiTypes.ParamsPostRightsRequest
): Promise<ApiTypes.ResPostRightsRequest> =>
  Call.post<ApiTypes.ResPostRightsRequest, ApiTypes.ParamsPostRightsRequest>(
    `/api/media/${mediaId}/rights-request`,
    params
  );

export const updateRightsRequest = (
  mediaId: string,
  params: ApiTypes.ParamsPostRightsRequest
): Promise<ApiTypes.ResPostRightsRequest> =>
  Call.post<ApiTypes.ResPostRightsRequest, ApiTypes.ParamsPostRightsRequest>(
    `/api/media/${mediaId}/rights-request`,
    params
  );

export const deleteRightsRequest = (mediaId: string): Promise<ApiTypes.ResDeleteRightsRequest> =>
  Call.delete(`/api/media/${mediaId}/rights-request`);

export const updateDiffusion = (media: Pick<MediaView, '_id' | 'diffusion'>) =>
  Call.post<
    MediaView,
    {
      mediaId: string;
      diffusion: boolean;
    }
  >('/api/media/diffusion', {
    mediaId: media._id,
    diffusion: !!media.diffusion,
  });

export const describeMedia = (
  params: ApiTypes.ParamsPostMediaDescribe
): Promise<ApiTypes.ResPostMediaDescribe> =>
  Call.post<ApiTypes.ResPostMediaDescribe, ApiTypes.ParamsPostMediaDescribe>(
    '/api/media/describe',
    params
  );
